import type { ReactNode } from "react";
import { Text, View } from "react-native";
import type { StyleProp, TextStyle, ViewStyle } from "react-native";
import { semanticColors } from "../theme/tokens";

type TextProps = {
  children: ReactNode;
  numberOfLines?: number;
  style?: StyleProp<TextStyle>;
};

/** Display title role reserved for dialog and sheet headers. */
export function DialogTitleText({ children, numberOfLines, style }: TextProps) {
  return (
    <Text
      className="font-dm-sans"
      numberOfLines={numberOfLines}
      style={[
        {
          fontSize: 20,
          lineHeight: 25,
          color: semanticColors.text.primary,
          fontWeight: "900",
          flexShrink: 1,
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}

/** Body/supporting role reserved for dialog and sheet explanatory copy. */
export function DialogBodyText({ children, numberOfLines, style }: TextProps) {
  return (
    <Text
      className="font-dm-sans"
      numberOfLines={numberOfLines}
      style={[
        {
          fontSize: 13,
          lineHeight: 18,
          color: semanticColors.text.secondary,
          marginTop: 6,
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}

type HeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  style?: StyleProp<ViewStyle>;
  titleStyle?: StyleProp<TextStyle>;
  subtitleStyle?: StyleProp<TextStyle>;
};

/** Shared title/body anatomy for dialogs and bottom sheets. */
export function DialogHeader({
  title,
  subtitle,
  leading,
  trailing,
  style,
  titleStyle,
  subtitleStyle,
}: HeaderProps) {
  return (
    <View style={[{ gap: 6 }, style]}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 10,
        }}
      >
        {leading}
        <DialogTitleText style={titleStyle}>{title}</DialogTitleText>
        {trailing}
      </View>
      {subtitle ? (
        <DialogBodyText style={[{ marginTop: 0 }, subtitleStyle]}>
          {subtitle}
        </DialogBodyText>
      ) : null}
    </View>
  );
}

export default DialogHeader;
